import {noop} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useMemo, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import {ConcentrationLayerData} from 'samples/plate-maps/concentration-layers/machine/ConcentrationLayerMachine';
import {
  ContextualDataParameterDeleteButton,
  ContextualDataParameterDropdown,
} from 'samples/plate-maps/display-layer-controls/DataParameterControls';
import {
  WellSchemaFieldDeleteButton,
  WellSchemaFieldDropdown,
} from 'samples/plate-maps/display-layer-controls/WellSchemaFieldControls';
import {
  ADDITIONAL_ATTRIBUTE_TO_DROPDOWN_OPTION,
  AssignmentGroup,
  AssignmentGroupValue,
  TEST_DILUTION_FACTOR_ATTRIBUTE_SENTINEL,
  DisplayLayerMetadata,
  DisplayLayerOptionSection,
  PLATE_MAP_MAX_CONCENTRATION_LAYERS,
  PLATE_MAP_MAX_DATA_PARAMETER_LAYERS,
  PLATE_MAP_MAX_WELL_SCHEMA_FIELD_LAYERS,
  PlateMapLegendItem,
  PlateMapWellDisplayLayer,
  PlateMap,
  PlateMapDisplayLayerAttribute,
  SchematizedAttribute,
  REAGENTS_ATTRIBUTE_SENTINEL,
  SchematizedDisplayLayerMetadata,
  WELL_ROLE_ATTRIBUTE_SENTINEL,
  CONCENTRATION_ATTRIBUTE_SENTINEL,
  ConcentrationAttribute,
} from 'samples/plate-maps/PlateMapConstants';
import {
  getDisplayLayerAttributeKey,
  getDisplayLayerForAttribute,
  getLegendItems,
  getMinMaxForNumberAttribute,
  getUnitStringForConcentrationLayer,
} from 'samples/plate-maps/PlateMapUtil';
import {getErrorKeyForDisplayLayerAttribute} from 'samples/plate-maps/validation/PlateMapValidationUtil';
import ValidationIcon from 'samples/plate-maps/validation/ValidationIcon';
import {
  PLATE_MAP_ERROR_LEVEL_PRECEDENCE,
  PlateMapAnnotationError,
  PlateMapErrorLevel,
  PlateMapErrorType,
  ValidationErrorsByLayerAndLevel,
} from 'samples/plate-maps/validation/ValidationTypes';
import {FONT_PROXIMA_13, FONT_PROXIMA_TITLE} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import useHoverState from 'ui-library/hooks/useHoverState';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Modal from 'ui-library/modals/Modal';
import runModal from 'ui-library/runModal';
import sortedBy from 'util/sortedBy';

import {PlateMapType} from '../annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import AddLayerButton from './AddLayerButton';

interface LayerPickerActions {
  sectionPlaceholder: JSX.Element;
  selectedOptionIds: Set<string>;
  onRemoveLayer: (idToRemove: string) => void;
  onToggleLayer: ({
    newSelectedIds,
    onLaunchConfirmDeleteModal,
    toggledAttribute,
  }: {
    newSelectedIds: Array<string>;
    onLaunchConfirmDeleteModal: () => void;
    toggledAttribute: SchematizedAttribute;
  }) => void;
}

interface SidebarSection {
  title: string;
  sectionKey: DisplayLayerOptionSection;
  availableOptions: Array<DisplayLayerMetadata>;
  /** Contains controls for adding & removing layers from this section if this behavior is supported */
  layerPickerActions: LayerPickerActions | null;
}

function SectionPlaceholder(props: {max: number; layerName: string}): JSX.Element {
  return (
    <div className={css(styles.placeholderText)}>
      Add up to {props.max} {props.layerName} layers to annotate on the plate map
    </div>
  );
}

/**
 * Returns the picker actions for the concentration layer section.
 *
 * @param concentrationLayersProps - The props for the concentration layers.
 * @returns The picker actions for the concentration layer section.
 */
function getConcentrationLayerPickerActions(
  concentrationLayersProps: ConcentrationLayersProps
): LayerPickerActions | null {
  if (!concentrationLayersProps.enableAddRemoveLayers) {
    return null;
  }

  return {
    sectionPlaceholder: (
      <SectionPlaceholder max={PLATE_MAP_MAX_CONCENTRATION_LAYERS} layerName="concentration" />
    ),
    selectedOptionIds: new Set(concentrationLayersProps.concentrationLayers.map((layer) => layer.layerId)),
    onToggleLayer: () => {
      concentrationLayersProps.onAddLayer();
    },
    onRemoveLayer: (layerId: string) => {
      concentrationLayersProps.onRemoveLayer(layerId);
    },
  };
}

/**
 * Returns the concentration attribute for a concentration layer.
 *
 * @param layer - The concentration layer.
 * @returns The concentration attribute.
 */
export function getConcentrationAttribute(layer: ConcentrationLayerData): ConcentrationAttribute {
  return {
    key: layer.layerId,
    type: CONCENTRATION_ATTRIBUTE_SENTINEL,
    name: layer.name,
  };
}

/**
 * Returns the options for the concentration layers section.
 *
 * @param concentrationLayers - The concentration layers.
 * @returns An array of display layer metadata for the concentration layers.
 */
function getConcentrationLayerOptions(
  concentrationLayers: Array<ConcentrationLayerData>
): Array<DisplayLayerMetadata> {
  return concentrationLayers.map((layer) => ({
    attribute: getConcentrationAttribute(layer),
    displayName: layer.name,
    icon: 'number',
  }));
}

/**
 * Returns the concentration layers section for the sidebar.
 *
 * @param concentrationLayersProps - The props for the concentration layers.
 * @returns The concentration layers section or an empty array if the flag is not enabled.
 */
function getConcentrationLayersSection(
  concentrationLayersProps?: ConcentrationLayersProps
): Array<SidebarSection> {
  if (!concentrationLayersProps || !BenchlingConfig.INVENTORY_ENABLE_PLATE_CONCENTRATION_LAYER) {
    return [];
  }
  const layerTitle = concentrationLayersProps.enableAddRemoveLayers
    ? `Concentration (${concentrationLayersProps.concentrationLayers.length}/${PLATE_MAP_MAX_CONCENTRATION_LAYERS})`
    : 'Concentration';

  return [
    {
      title: layerTitle,
      sectionKey: DisplayLayerOptionSection.CONCENTRATION,
      availableOptions: getConcentrationLayerOptions(concentrationLayersProps.concentrationLayers),
      layerPickerActions: getConcentrationLayerPickerActions(concentrationLayersProps),
    },
  ];
}

function getSections(
  wellSchemaLayersProps: PickableLayersProps,
  parameterLayerProps: PickableLayersProps,
  concentrationLayersProps?: ConcentrationLayersProps
): Array<SidebarSection> {
  return [
    {
      title: 'Role',
      sectionKey: DisplayLayerOptionSection.WELL_ROLE,
      availableOptions: [ADDITIONAL_ATTRIBUTE_TO_DROPDOWN_OPTION[WELL_ROLE_ATTRIBUTE_SENTINEL]],
      layerPickerActions: null,
    },
    {
      title: 'Contents',
      sectionKey: DisplayLayerOptionSection.CONTENTS,
      availableOptions: [ADDITIONAL_ATTRIBUTE_TO_DROPDOWN_OPTION[REAGENTS_ATTRIBUTE_SENTINEL]],
      layerPickerActions: null,
    },
    ...getConcentrationLayersSection(concentrationLayersProps),
    {
      title: 'Test metadata',
      sectionKey: DisplayLayerOptionSection.TEST_METADATA,
      availableOptions: [ADDITIONAL_ATTRIBUTE_TO_DROPDOWN_OPTION[TEST_DILUTION_FACTOR_ATTRIBUTE_SENTINEL]],
      layerPickerActions: null,
    },
    {
      title: wellSchemaLayersProps.enableAddRemoveLayers
        ? `Schema fields (${wellSchemaLayersProps.selectedLayerIds.size}/${PLATE_MAP_MAX_WELL_SCHEMA_FIELD_LAYERS})`
        : 'Schema fields',
      sectionKey: DisplayLayerOptionSection.SCHEMA_FIELDS,
      availableOptions: wellSchemaLayersProps.availableLayers,
      layerPickerActions: wellSchemaLayersProps.enableAddRemoveLayers
        ? {
            sectionPlaceholder: (
              <SectionPlaceholder
                max={PLATE_MAP_MAX_WELL_SCHEMA_FIELD_LAYERS}
                layerName="well schema field"
              />
            ),
            selectedOptionIds: wellSchemaLayersProps.selectedLayerIds,
            onRemoveLayer: wellSchemaLayersProps.onRemoveLayer,
            onToggleLayer: wellSchemaLayersProps.onToggleLayer,
          }
        : null,
    },
    {
      title: parameterLayerProps.enableAddRemoveLayers
        ? `Parameters (${parameterLayerProps.selectedLayerIds.size}/${PLATE_MAP_MAX_DATA_PARAMETER_LAYERS})`
        : 'Parameters',
      sectionKey: DisplayLayerOptionSection.PARAMETERS,
      availableOptions: parameterLayerProps.availableLayers,
      layerPickerActions: parameterLayerProps.enableAddRemoveLayers
        ? {
            sectionPlaceholder: (
              <SectionPlaceholder max={PLATE_MAP_MAX_DATA_PARAMETER_LAYERS} layerName="data parameter" />
            ),
            selectedOptionIds: parameterLayerProps.selectedLayerIds,
            onRemoveLayer: parameterLayerProps.onRemoveLayer,
            onToggleLayer: parameterLayerProps.onToggleLayer,
          }
        : null,
    },
  ];
}

export function DeleteConcentrationLayerConfirmationModal({
  layerName,
  onRequestSubmit,
  onRequestClose,
}: {
  layerName: string;
  onRequestSubmit: () => void;
  onRequestClose: () => void;
}): JSX.Element {
  return (
    <Modal
      title="Remove concentration layer?"
      onRequestSubmit={onRequestSubmit}
      onRequestClose={onRequestClose}
      buttons={[
        {
          style: ButtonStyle.PRIMARY,
          submit: true,
          text: 'Yes, remove layer',
        },
      ]}
    >
      <p>
        Changes made to "{layerName}" will not be saved. Are you sure you want to remove the concentration
        layer?
      </p>
    </Modal>
  );
}

/**
 * Gets the unit string for a concentration attribute.
 */
function getUnitStringForConcentrationAttribute(
  plateMap: PlateMap,
  attribute: ConcentrationAttribute
): string {
  const layerId = attribute.key;
  return getUnitStringForConcentrationLayer(plateMap, layerId);
}

function getLegendItemsForNumberAttribute(
  plateMap: PlateMap,
  displayLayer: PlateMapWellDisplayLayer,
  attribute: PlateMapDisplayLayerAttribute
): Array<PlateMapLegendItem> {
  const minMax = getMinMaxForNumberAttribute(plateMap, attribute);
  if (minMax == null) {
    return [];
  }
  const {minValue, maxValue, totalAssigned} = minMax;
  // Concatenate the unit string to the label if the display layer is a concentration layer.
  const unitString =
    displayLayer === PlateMapWellDisplayLayer.CONCENTRATION
      ? ` ${getUnitStringForConcentrationAttribute(plateMap, attribute as ConcentrationAttribute)}`
      : '';
  const label = minValue === maxValue ? `${minValue}${unitString}` : `${minValue} - ${maxValue}${unitString}`;
  return [{label, icon: null, count: totalAssigned}];
}

function LayerSelectorOptionGroups({
  plateMap,
  attribute,
  assignmentGroupsById,
}: {
  plateMap: PlateMap;
  attribute: PlateMapDisplayLayerAttribute;
  assignmentGroupsById: {[id: string]: AssignmentGroup<AssignmentGroupValue>};
}): JSX.Element | null {
  function getLayerOptionGroups(): Array<PlateMapLegendItem> {
    // Handle number fields slightly differently from in the legend, show only one group as a range.
    const displayLayer = getDisplayLayerForAttribute(attribute);
    if (
      displayLayer === PlateMapWellDisplayLayer.SCHEMA_FIELD_NUMBER ||
      displayLayer === PlateMapWellDisplayLayer.CONCENTRATION
    ) {
      return getLegendItemsForNumberAttribute(plateMap, displayLayer, attribute);
    }
    return getLegendItems({
      plateMap,
      displayLayer: getDisplayLayerForAttribute(attribute),
      assignmentGroupsById: assignmentGroupsById ?? {},
      attribute,
      groupedValidationErrors: {},
    });
  }

  const legendItems = useMemo(getLayerOptionGroups, [plateMap, assignmentGroupsById, attribute]);
  const groupText = attribute === WELL_ROLE_ATTRIBUTE_SENTINEL ? 'group' : 'well';
  return (
    <div className={css(styles.optionGroupContainer)}>
      {legendItems.length === 0 ? (
        <span>No values assigned</span>
      ) : (
        <>
          {legendItems.map(({label, count}) => (
            <div className={css(styles.optionGroup)} key={label}>
              <span>{label}</span>
              <span className={css(styles.optionGroupSubtext)}>{`${count} ${pluralize(
                count,
                groupText
              )}`}</span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function ConcentrationLayerDeleteButton({
  layerAttribute,
  onRemoveLayer,
}: {
  layerAttribute: ConcentrationAttribute;
  onRemoveLayer: (layerId: string) => void;
}): JSX.Element {
  return (
    <BenchlingIcon
      icon="xmark"
      style={{color: Color.GRAY_500}}
      onClick={(e) => {
        e.stopPropagation();
        // Render a confirmation modal before removing the layer
        runModal(DeleteConcentrationLayerConfirmationModal, {
          layerName: layerAttribute.name,
          onRequestSubmit: () => {
            onRemoveLayer(layerAttribute.key);
          },
          onRequestClose: noop,
        });
      }}
    />
  );
}

function LayerSelectorOption({
  sectionKey,
  plateMap,
  layerOption,
  onSelectAttribute,
  assignmentGroupsById,
  isSelected,
  isDisabled,
  pickerActions,
  error,
}: {
  sectionKey: DisplayLayerOptionSection;
  plateMap: PlateMap;
  layerOption: DisplayLayerMetadata;
  onSelectAttribute: (attr: PlateMapDisplayLayerAttribute) => void;
  assignmentGroupsById: {[id: string]: AssignmentGroup<AssignmentGroupValue>};
  isSelected: boolean;
  isDisabled: boolean;
  pickerActions: LayerPickerActions | null;
  error: PlateMapAnnotationError | null;
}): JSX.Element {
  const [isHovered, hoverProps] = useHoverState();
  const [shouldExpand, setShouldExpand] = useState(true);
  const isExpanded = shouldExpand && isSelected;
  const shouldShowDeleteButton = isHovered && pickerActions != null;

  function toggleOptionGroups(): void {
    if (isSelected) {
      setShouldExpand(!shouldExpand);
    }
  }

  const shouldShowConcentrationLayerDeleteButton =
    shouldShowDeleteButton &&
    sectionKey === DisplayLayerOptionSection.CONCENTRATION &&
    pickerActions?.onRemoveLayer != null;

  return (
    <div
      className={classNames(css(styles.displayLayerOption), {
        [css(styles.selectedDisplayLayerOption)]: isSelected,
        [css(styles.disabledDisplayLayerOption)]: isDisabled,
      })}
      onClick={() => {
        if (!isDisabled) {
          onSelectAttribute(layerOption.attribute);
        }
      }}
      {...hoverProps}
      {...getTestProps({
        component: 'PlateMapDisplayLayerSidebar',
        element: 'LayerSelectorOption',
        key: layerOption.displayName,
      })}
    >
      <div className={css(styles.optionHeader)}>
        <span>
          <BenchlingIcon
            className={css(styles.optionCaret)}
            icon={isExpanded ? 'caret-down' : 'caret-right'}
            onClick={toggleOptionGroups}
          />
          <BenchlingIcon
            className={classNames(css(styles.optionIcon), {
              [css(styles.selectedOptionIcon)]: isSelected,
              [css(styles.disabledOptionColor)]: isDisabled,
            })}
            icon={layerOption.icon}
          />
          <span
            className={classNames({
              [css(styles.disabledOptionColor)]: isDisabled,
            })}
          >
            {layerOption.displayName}
          </span>
          {error != null && (
            <span className={css(styles.validationIconWrapper)}>
              <Tooltip
                text={`${error.message} Remove layer.`}
                isDisabled={error.type !== PlateMapErrorType.LAYER}
              >
                <ValidationIcon level={error.level} size={12} />
              </Tooltip>
            </span>
          )}
        </span>
        {shouldShowDeleteButton && sectionKey === DisplayLayerOptionSection.SCHEMA_FIELDS && (
          <WellSchemaFieldDeleteButton
            schemaFieldId={(layerOption.attribute as SchematizedAttribute).key}
            onRemoveLayer={pickerActions.onRemoveLayer}
          />
        )}
        {shouldShowConcentrationLayerDeleteButton && (
          <ConcentrationLayerDeleteButton
            layerAttribute={layerOption.attribute as ConcentrationAttribute}
            onRemoveLayer={pickerActions.onRemoveLayer}
          />
        )}
        {shouldShowDeleteButton && sectionKey === DisplayLayerOptionSection.PARAMETERS && (
          <ContextualDataParameterDeleteButton
            versionId={(layerOption.attribute as SchematizedAttribute).key}
            onRemoveLayer={pickerActions.onRemoveLayer}
          />
        )}
      </div>
      {isExpanded && (
        <LayerSelectorOptionGroups
          plateMap={plateMap}
          attribute={layerOption.attribute}
          assignmentGroupsById={assignmentGroupsById}
        />
      )}
    </div>
  );
}

// Exported for testing
export function LayerSelectorSection({
  title,
  sectionKey,
  availableOptions,
  plateMap,
  setSelectedAttribute,
  assignmentGroups,
  isDisabled,
  selectedAttributeKey,
  pickerActions,
  groupedValidationErrors,
  concentrationLayersProps,
}: {
  title: string;
  sectionKey: DisplayLayerOptionSection;
  availableOptions: Array<DisplayLayerMetadata>;
  plateMap: PlateMap;
  setSelectedAttribute: (attr: PlateMapDisplayLayerAttribute) => void;
  assignmentGroups: {[key: string]: {[id: string]: AssignmentGroup<AssignmentGroupValue>}};
  isDisabled: boolean;
  selectedAttributeKey: string;
  pickerActions: LayerPickerActions | null;
  groupedValidationErrors: ValidationErrorsByLayerAndLevel;
  concentrationLayersProps?: ConcentrationLayersProps;
}): JSX.Element | null {
  if (availableOptions.length === 0 && pickerActions == null) {
    return null;
  }

  const selectedOptions =
    pickerActions != null
      ? availableOptions.filter((option) =>
          pickerActions.selectedOptionIds.has(
            (option.attribute as SchematizedAttribute | ConcentrationAttribute).key
          )
        )
      : availableOptions;

  function getWorstErrorForLayer(attribute: PlateMapDisplayLayerAttribute): PlateMapAnnotationError | null {
    const validationErrors = groupedValidationErrors[getErrorKeyForDisplayLayerAttribute(attribute)] ?? {};
    const errorLevels = Object.keys(validationErrors) as Array<PlateMapErrorLevel>;
    if (errorLevels.length === 0) {
      return null;
    }
    const sortedErrorLevels = sortedBy(errorLevels, [
      // sortedBy sorts in ascending order and we want descending order.
      (errorLevel) => -PLATE_MAP_ERROR_LEVEL_PRECEDENCE[errorLevel],
    ]);
    const errorsAtWorstLevel = validationErrors[sortedErrorLevels[0]] ?? [];
    // If there is a layer error, that is the worst.
    const maybeLayerError = errorsAtWorstLevel.find(({error}) => error.type === PlateMapErrorType.LAYER);
    if (maybeLayerError == null) {
      return errorsAtWorstLevel.length > 0 ? errorsAtWorstLevel[0].error : null;
    }
    return maybeLayerError.error;
  }

  return (
    <div
      className={css(styles.sidebarSection)}
      {...getTestProps({
        component: 'PlateMapDisplayLayerSidebar',
        element: 'LayerSelectorSection',
        key: sectionKey,
      })}
    >
      <div className={css(styles.titleWrapper)}>
        <div className={css(styles.sectionTitle)}>{title}</div>
        {pickerActions != null && (
          <div className={css(styles.parameterDropdownWrapper)}>
            {sectionKey === DisplayLayerOptionSection.SCHEMA_FIELDS && (
              <WellSchemaFieldDropdown
                onToggleLayer={pickerActions.onToggleLayer}
                schemaFieldOptions={availableOptions as Array<SchematizedDisplayLayerMetadata>}
                selectedValues={selectedOptions.map(
                  (option) => (option.attribute as SchematizedAttribute).key
                )}
              />
            )}
            {sectionKey === DisplayLayerOptionSection.PARAMETERS && (
              <ContextualDataParameterDropdown
                dataParameterOptions={availableOptions as Array<SchematizedDisplayLayerMetadata>}
                selectedVersionId={pickerActions.selectedOptionIds}
                onToggleLayer={pickerActions.onToggleLayer}
              />
            )}
            {sectionKey === DisplayLayerOptionSection.CONCENTRATION && concentrationLayersProps && (
              <AddLayerButton
                disabled={
                  concentrationLayersProps.concentrationLayers.length >= PLATE_MAP_MAX_CONCENTRATION_LAYERS
                }
                onClick={() => {
                  concentrationLayersProps.onAddLayer();
                }}
              />
            )}
          </div>
        )}
      </div>
      {selectedOptions.map((option) => {
        const key = getDisplayLayerAttributeKey(option.attribute);
        const worstError = getWorstErrorForLayer(option.attribute);
        return (
          <LayerSelectorOption
            key={key}
            sectionKey={sectionKey}
            plateMap={plateMap}
            layerOption={option}
            onSelectAttribute={setSelectedAttribute}
            assignmentGroupsById={assignmentGroups[key]}
            isSelected={selectedAttributeKey === key}
            isDisabled={isDisabled}
            pickerActions={pickerActions}
            error={worstError}
          />
        );
      })}
      {pickerActions != null &&
        pickerActions.selectedOptionIds.size === 0 &&
        (availableOptions.length > 0 ||
          // Display placeholder for concentration when there are no concentration layers
          (sectionKey === DisplayLayerOptionSection.CONCENTRATION &&
            concentrationLayersProps?.concentrationLayers.length === 0)) &&
        pickerActions.sectionPlaceholder}
    </div>
  );
}

export interface PickableLayersProps {
  enableAddRemoveLayers: boolean;
  availableLayers: Array<DisplayLayerMetadata>;
  selectedLayerIds: Set<string>;
  onToggleLayer: ({
    newSelectedIds,
    onLaunchConfirmDeleteModal,
    toggledAttribute,
  }: {
    newSelectedIds: Array<string>;
    onLaunchConfirmDeleteModal: () => void;
    toggledAttribute: SchematizedAttribute;
  }) => void;
  onRemoveLayer: (idToRemove: string) => void;
}

export interface ConcentrationLayersProps {
  enableAddRemoveLayers: boolean;
  concentrationLayers: Array<ConcentrationLayerData>;
  activeLayerId: string | null;
  onAddLayer: () => void;
  onRemoveLayer: (layerId: string) => void;
  onSelectLayer: (layerId: string | null) => void;
}

export interface PlateMapDisplayLayerSidebarProps {
  plateMap: PlateMap;
  selectedAttributeKey: string;
  setSelectedAttribute: (attr: PlateMapDisplayLayerAttribute) => void;
  assignmentGroups: {[attrKey: string]: {[id: string]: AssignmentGroup<AssignmentGroupValue>}};
  disabledSections: Array<DisplayLayerOptionSection>;
  /* Sections to be hidden entirely, rather than shown but disabled (as above). */
  hiddenSections?: Array<DisplayLayerOptionSection>;
  groupedValidationErrors: ValidationErrorsByLayerAndLevel;
  wellSchemaLayersProps: PickableLayersProps;
  plateMapType: PlateMapType;
  parameterLayersProps?: PickableLayersProps;
  concentrationLayersProps?: ConcentrationLayersProps;
  additionalContent?: JSX.Element;
}

/**
 * Sidebar component for selecting plate map display layers.
 * Display layer options include well roles, contents and sources, an option for each schema field
 * on the plate cell's container schema, and any given parameter options.
 */
export default function PlateMapDisplayLayerSidebar({
  plateMap,
  selectedAttributeKey,
  setSelectedAttribute,
  assignmentGroups,
  disabledSections,
  hiddenSections,
  groupedValidationErrors,
  wellSchemaLayersProps,
  parameterLayersProps = {
    enableAddRemoveLayers: false,
    availableLayers: [],
    selectedLayerIds: new Set(),
    onToggleLayer: noop,
    onRemoveLayer: noop,
  },
  concentrationLayersProps,
  plateMapType,
  additionalContent,
}: PlateMapDisplayLayerSidebarProps): JSX.Element {
  // If we ever get in a state where we have an attribute selected that doesn't exist (e.g. because we've just
  // deleted the data parameter layer we were looking at), fall back to well roles.
  // Also auto-select concentration layer attributes when the active concentration layer changes.
  useEffect(() => {
    const selectableAttributeKeys = [
      WELL_ROLE_ATTRIBUTE_SENTINEL,
      REAGENTS_ATTRIBUTE_SENTINEL,
      TEST_DILUTION_FACTOR_ATTRIBUTE_SENTINEL,
      ...wellSchemaLayersProps.availableLayers
        .filter((layer) =>
          wellSchemaLayersProps.selectedLayerIds.has((layer.attribute as SchematizedAttribute).key)
        )
        .map((layer) => getDisplayLayerAttributeKey(layer.attribute)),
      ...parameterLayersProps.availableLayers
        .filter((parameter) =>
          parameterLayersProps.selectedLayerIds.has((parameter.attribute as SchematizedAttribute).key)
        )
        .map((parameter) => getDisplayLayerAttributeKey(parameter.attribute)),
      ...(concentrationLayersProps?.concentrationLayers.map((layer) =>
        getDisplayLayerAttributeKey(getConcentrationAttribute(layer))
      ) ?? []),
    ];

    // Auto-select the active concentration layer attribute when it changes
    if (concentrationLayersProps?.activeLayerId != null) {
      const activeLayer = concentrationLayersProps.concentrationLayers.find(
        (layer) => layer.layerId === concentrationLayersProps.activeLayerId
      );
      if (activeLayer) {
        const activeConcentrationAttribute = getConcentrationAttribute(activeLayer);
        const activeConcentrationAttributeKey = getDisplayLayerAttributeKey(activeConcentrationAttribute);
        // Only auto-select if the current selection is not already the active concentration layer
        // This prevents infinite loops from the effect triggering itself
        if (selectedAttributeKey !== activeConcentrationAttributeKey) {
          setSelectedAttribute(activeConcentrationAttribute);
          return;
        }
      }
    }

    // Fallback: if the currently selected attribute no longer exists, select well roles
    if (!selectableAttributeKeys.includes(selectedAttributeKey)) {
      setSelectedAttribute(WELL_ROLE_ATTRIBUTE_SENTINEL);
    }
  }, [
    parameterLayersProps.availableLayers,
    parameterLayersProps.selectedLayerIds,
    wellSchemaLayersProps.availableLayers,
    wellSchemaLayersProps.selectedLayerIds,
    concentrationLayersProps?.concentrationLayers,
    concentrationLayersProps?.activeLayerId,
    selectedAttributeKey,
    setSelectedAttribute,
  ]);

  const handleSetSelectedAttribute = (attr: PlateMapDisplayLayerAttribute): void => {
    if (concentrationLayersProps) {
      if (typeof attr === 'object' && attr.type === CONCENTRATION_ATTRIBUTE_SENTINEL) {
        concentrationLayersProps.onSelectLayer(attr.key);
      } else {
        concentrationLayersProps.onSelectLayer(null);
      }
    }
    // Handle normal layer selection
    setSelectedAttribute(attr);
  };

  const sections = getSections(wellSchemaLayersProps, parameterLayersProps, concentrationLayersProps).filter(
    (section) => !(hiddenSections ?? []).includes(section.sectionKey)
  );
  return (
    <>
      {additionalContent}
      {sections.map(({title, availableOptions, sectionKey, layerPickerActions}) => (
        <LayerSelectorSection
          key={sectionKey}
          title={title}
          sectionKey={sectionKey}
          availableOptions={availableOptions}
          plateMap={plateMap}
          setSelectedAttribute={handleSetSelectedAttribute}
          assignmentGroups={assignmentGroups}
          isDisabled={disabledSections.includes(sectionKey)}
          selectedAttributeKey={selectedAttributeKey}
          pickerActions={layerPickerActions}
          groupedValidationErrors={groupedValidationErrors}
          concentrationLayersProps={concentrationLayersProps}
        />
      ))}
    </>
  );
}

const styles = StyleSheet.create({
  sidebarSection: {},
  titleWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    height: '24px',
  },
  sectionTitle: {
    ...FONT_PROXIMA_TITLE,
    // Override the default line-height to center-align the text vertically so it lines up with the add button
    lineHeight: '24px',
    textTransform: 'uppercase',
    color: GRAY_DARKER,
    letterSpacing: 1,
    flex: '0 0 auto',
  },
  validationIcon: {marginLeft: '16px'},
  parameterDropdownWrapper: {
    flex: '0 0 auto',
  },
  placeholderText: {
    ...FONT_PROXIMA_13,
    color: Color.GRAY_700,
    marginBottom: '8px',
  },
  displayLayerOption: {
    padding: '8px 12px',
    ':hover': {
      backgroundColor: Color.GRAY_50,
    },
    cursor: 'pointer',
  },
  selectedDisplayLayerOption: {
    backgroundColor: Color.BLUE_100,
    ':hover': {
      backgroundColor: Color.BLUE_100,
    },
  },
  disabledDisplayLayerOption: {
    cursor: 'default',
    color: Color.GRAY_500,
  },
  optionHeader: {
    ...FONT_PROXIMA_TITLE,
    display: 'flex',
    color: Color.GRAY_900,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  optionCaret: {
    color: Color.GRAY_500,
  },
  optionIcon: {
    margin: '0 8px',
    color: Color.GRAY_600,
  },
  selectedOptionIcon: {
    color: Color.BLUE_450,
  },
  disabledOptionColor: {
    color: Color.GRAY_500,
  },
  validationIconWrapper: {
    marginLeft: '12px',
  },
  optionGroupContainer: {
    marginLeft: 14,
  },
  optionGroup: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 4,
    alignItems: 'center',
  },
  optionGroupSubtext: {
    color: Color.GRAY_700,
    textWrap: 'nowrap',
  },

  plateMapTypeBadgeContainer: {
    position: 'relative',
    height: '34px',
  },
  plateMapTypeBadge: {
    position: 'absolute',
    transition: 'transform 500ms ease-in-out',
    transform: 'translateX(0px)',
    zIndex: 2,
  },
  plateMapTypeBadgeFloat: {
    transform: 'translateX(40px)',
  },
});
